import React, { useEffect, useState } from 'react';
import { PageTitle } from '../../layout/core';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { GoBackBtn } from '../header/GoBackBtn';
import { ScriptComponent } from '../developers/ScriptComponent';
import { Spinner } from '../Spinner';
import { ActivityTimeline } from './ActivityTimeline';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { getFormattedDate } from '../../utils/utilities';
import { ActivityLogSection } from './ActivitiLogSection';
import {
	fetchOrderActivityLogData,
	fetchOrderActivityLists,
	fetchArchivedOrderActivityLogData,
} from '../../store/order/actions/activity-log';
import { useOrdersContext } from '../../store/order/order-context';
import { ActivityLogFilters } from './ActivityLogFilters';

import './ActivityLog.scss';
import { useFormik } from 'formik';
import {
	convertToOrderActivityLogInitial,
	IActivityLogsDetails,
	initialValues,
} from '../../constants/activity-log/activity-log-form';

export const ActivityLog = (): React.ReactElement => {
	const { t } = useTranslation();
	const { id } = useParams();

	const { orderContextState, dispatch } = useOrdersContext();
	const { orderActivityLogData, isOrderActivityLogDataLoading, orderDetailsData } = orderContextState;

	const [script, setScript] = useState<string>('');
	const [checkedItemId, setCheckedItemId] = useState<number>();
	const [isArchiveModalOpen, setIsArchiveModalOpen] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const formik = useFormik<IActivityLogsDetails>({
		initialValues,
		onSubmit: async () => {
			setScript('');
			await fetchOrderActivityLogData(dispatch, id, formik);
		}
	});

	const groupByDate = orderActivityLogData?.reduce((group, item, index) => {
		const date = getFormattedDate(item.createdOn, 'yyyy-MM-dd') as string;
		if (!group[date]) {
			group[date] = [{ ...item, id: index + 1 }];
		} else {
			group[date].push({ ...item, id: index + 1 });
		}
		return group;
	}, {});

	const newActivityList = orderActivityLogData?.map((item, index) => ({ ...item, id: index + 1 }));

	useEffect(() => {
		void fetchOrderActivityLists(dispatch);
		void fetchOrderActivityLogData(dispatch, id);
	}, []);

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));
	const closeArchiveModal = (): void => setIsArchiveModalOpen(false);

	const openModal = (script: string): void => {
		setModalState({
			isOpen: true,
			modalTitle: 'Activity information',
			contentClassName: 'd-block activity-log-mobile-scrollbar',
			content: (
				<div className="sense-script m-6">
					<pre className="code-area p-5">
						{script}
					</pre>
				</div>
			)
		});
	};

	const timeLineBody = Object.keys(groupByDate ?? {})?.length > 1
		? (
			Object.keys(groupByDate || {})?.map((key): JSX.Element => (
				<ActivityLogSection
					key={key}
					date={key}
					data={groupByDate[key]}
					openModal={openModal}
					setScript={setScript}
					checkedItemId={checkedItemId}
					setCheckedItemId={setCheckedItemId}
				/>
			))
		)
		: (
			<ActivityTimeline
				activityList={newActivityList}
				openModal={openModal}
				openArchiveModal={(): void => setIsArchiveModalOpen(true)}
				setScript={setScript}
				checkedItemId={checkedItemId}
				setCheckedItemId={setCheckedItemId}
			/>
		);

	const confirmArchiveLoading = async (): Promise<void> => {
		const data = convertToOrderActivityLogInitial();
		await formik.setValues(data);
		await fetchArchivedOrderActivityLogData(dispatch, id, orderDetailsData?.createdOn);
		closeArchiveModal();
	};

	//TODO add translations
	return (
		<>
			<PageTitle
				breadcrumbs={[
					{
						title: t('ORDERS.ACTIVITY_TITLE'),
						path: '/orders',
						isSeparator: false,
						isActive: false
					},
					{
						title: '',
						path: '',
						isSeparator: true,
						isActive: false
					}
				]}
			>
				{id}
			</PageTitle>
			<div className="mb-5">
				<GoBackBtn path={null}/>
			</div>

			<ActivityLogFilters formik={formik}/>

			<div className="card position-relative">
				<div className="d-md-flex justify-content-md-between">
					<div className="card-body col-md-4 scrollbar">
						{
							isOrderActivityLogDataLoading
								? <Spinner pageLoader={true}/>
								: timeLineBody
						}
					</div>
					<div className="card-body col-md-6">
						{
							script && (
								<ScriptComponent
									title="Activity information"
									script={script ? script: 'No preview'}
									copy={!!script}
									additionalClass={!script && 'text-center fs-6'}
								/>
							)
						}
					</div>
				</div>
			</div>
			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>
			<ModalWindow
				isOpen={isArchiveModalOpen}
				onClose={closeArchiveModal}
				modalTitle='Load from archive'
				modalText='The operation you are about to execute is resource-intensive.'
				cancelButtonText='Back'
				onCancel={closeArchiveModal}
				confirmButtonText='Ok'
				onConfirm={confirmArchiveLoading}
			/>


		</>
	);
};
